import { useState } from 'react';
import { useAlerts, useCreateAlert, useUpdateAlert, useDeleteAlert } from '@/lib/api/hooks/useAlerts';
import { useCanWrite } from '@/lib/auth/AuthProvider';
import type { AlertConfig, AlertConfigCreate, AlertMetric, AlertComparison } from '@/types/analytics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

const METRIC_LABELS: Record<AlertMetric, string> = {
  show_rate: 'Show Rate (%)',
  close_rate: 'Close Rate (%)',
  time_to_first_call: 'Time to First Call (min)',
  stale_leads_count: 'Stale Leads Count',
  cpl: 'Cost Per Lead ($)',
};

const COMPARISON_LABELS: Record<AlertComparison, string> = {
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
};

// ---------------------------------------------------------------------------
// Empty form state
// ---------------------------------------------------------------------------

interface FormState {
  metric: AlertMetric;
  threshold: string;
  comparison: AlertComparison;
  notification_recipients: string;
  enabled: boolean;
}

const DEFAULT_FORM: FormState = {
  metric: 'show_rate',
  threshold: '',
  comparison: 'lt',
  notification_recipients: '',
  enabled: true,
};

function alertToForm(a: AlertConfig): FormState {
  return {
    metric: a.metric,
    threshold: String(a.threshold),
    comparison: a.comparison,
    notification_recipients: a.notification_recipients.join(', '),
    enabled: a.enabled,
  };
}

// ---------------------------------------------------------------------------
// AlertsManager
// ---------------------------------------------------------------------------

export function AlertsManager() {
  const canWrite = useCanWrite();
  const { data: alerts = [], isLoading, error } = useAlerts();
  const createAlert = useCreateAlert();
  const updateAlert = useUpdateAlert();
  const deleteAlert = useDeleteAlert();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  function openCreate() {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(alert: AlertConfig) {
    setEditingId(alert.id);
    setForm(alertToForm(alert));
    setFormError(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setFormError(null);
  }

  function parseForm(): AlertConfigCreate | null {
    const threshold = parseFloat(form.threshold);
    if (isNaN(threshold)) {
      setFormError('Threshold must be a number.');
      return null;
    }
    const recipients = form.notification_recipients
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (recipients.length === 0) {
      setFormError('At least one recipient email is required.');
      return null;
    }
    return {
      metric: form.metric,
      threshold,
      comparison: form.comparison,
      notification_channels: ['email'],
      notification_recipients: recipients,
      enabled: form.enabled,
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const payload = parseForm();
    if (!payload) return;

    try {
      if (editingId) {
        await updateAlert.mutateAsync({ id: editingId, data: payload });
      } else {
        await createAlert.mutateAsync(payload);
      }
      closeForm();
    } catch {
      setFormError('Failed to save alert. Please try again.');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this alert?')) return;
    try {
      await deleteAlert.mutateAsync(id);
    } catch {
      // error surfaced via mutation state if needed
    }
  }

  async function handleToggle(alert: AlertConfig) {
    try {
      await updateAlert.mutateAsync({ id: alert.id, data: { enabled: !alert.enabled } });
    } catch {
      // ignore
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Threshold Alerts</h3>
        {canWrite && !showForm && (
          <Button size="sm" variant="outline" onClick={openCreate}>
            + New Alert
          </Button>
        )}
      </div>

      {/* Inline form */}
      {showForm && canWrite && (
        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-border bg-muted/50 p-4 space-y-3"
        >
          <p className="text-sm font-medium text-foreground">
            {editingId ? 'Edit Alert' : 'New Alert'}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Metric</Label>
              <Select
                value={form.metric}
                onValueChange={(v) => setForm({ ...form, metric: v as AlertMetric })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(METRIC_LABELS) as [AlertMetric, string][]).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.metric === 'cpl' && (
                <p className="text-xs text-amber-600">
                  CPL alerts require Meta Ads integration and will not fire until that feature is enabled.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Condition</Label>
              <Select
                value={form.comparison}
                onValueChange={(v) => setForm({ ...form, comparison: v as AlertComparison })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(COMPARISON_LABELS) as [AlertComparison, string][]).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="threshold" className="text-xs">Threshold</Label>
            <Input
              id="threshold"
              type="number"
              step="any"
              placeholder="e.g. 60"
              value={form.threshold}
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
              className="text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="recipients" className="text-xs">
              Recipients (comma-separated emails)
            </Label>
            <Input
              id="recipients"
              type="text"
              placeholder="you@example.com, teammate@example.com"
              value={form.notification_recipients}
              onChange={(e) => setForm({ ...form, notification_recipients: e.target.value })}
              className="text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="h-4 w-4"
            />
            <Label htmlFor="enabled" className="text-xs cursor-pointer">Enabled</Label>
          </div>

          {formError && <p className="text-xs text-red-600">{formError}</p>}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              size="sm"
              disabled={createAlert.isPending || updateAlert.isPending}
            >
              {editingId ? 'Save' : 'Create'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={closeForm}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Alert list */}
      {isLoading && <p className="text-sm text-muted-foreground">Loading alerts…</p>}
      {error && <p className="text-sm text-red-500">Failed to load alerts.</p>}

      {!isLoading && alerts.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground">
          No alerts configured.{canWrite ? ' Create one to get notified when KPIs drop.' : ''}
        </p>
      )}

      {alerts.map((alert) => (
        <div
          key={alert.id}
          className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3"
        >
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">
              {METRIC_LABELS[alert.metric]}{' '}
              <span className="text-muted-foreground">
                {COMPARISON_LABELS[alert.comparison]} {alert.threshold}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              {alert.notification_recipients.join(', ')}
              {alert.last_triggered_at && (
                <> &middot; Last fired {new Date(alert.last_triggered_at).toLocaleDateString()}</>
              )}
              {alert.last_value !== null && (
                <> &middot; Last value: {alert.last_value.toFixed(1)}</>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                alert.enabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {alert.enabled ? 'On' : 'Off'}
            </span>
            {canWrite && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 px-2"
                  onClick={() => handleToggle(alert)}
                  disabled={updateAlert.isPending}
                >
                  {alert.enabled ? 'Pause' : 'Enable'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 px-2"
                  onClick={() => openEdit(alert)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 px-2 text-red-500 hover:text-red-600"
                  onClick={() => handleDelete(alert.id)}
                  disabled={deleteAlert.isPending}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
